/**
 * /chemistry-calculator — химия школьника: уравнять реакцию, молярная масса и
 * состав, задача «по уравнению», степени окисления, растворы и газы.
 *
 * Главное отличие от «балансировщиков»: ответ приходит вместе с ЗАПИСЬЮ, за
 * которую ставят оценку, — таблица атомов до и после, алгебраический способ,
 * электронный баланс со степенями окисления над символами, задача в
 * оформлении «Дано / Найти / Решение / Ответ». Движок — чистые модули рядом
 * (formula, equation, balance, oxidation, redox, molar, stoich), этот файл
 * только связывает их со страницей.
 *
 * ⚠️ Сторожа: состояние видно снаружи (window.__chmState) — стенд читает его,
 * а не пиксели.
 */

import { CFG, $, t, esc, F, TABLE, LOCALE, LABEL_SEP, speciesHtml, speciesText, equationHtml, equationText as eqTextU, ARROWS,
    stage, ae, funnel, complete, diag, caught, critical, settleNow, settleCancel, ensureScript, toast, flash, settings, elName,
    defaultMassMode, avogadro, units, oxShow, oxEqShow, oxSupText, OX_NOTATION, LIST_SEP, LIST_COMMA, ltrMath, locText } from './ui-common.js';
import { FormulaError, formulaUnicode, subDigits, normalizeChars } from './formula.js';
import { parseEquation, pickSpecies, suggestCase, suggestZero, suggestZeroSpecies, oddSimple, leadingCoef, speciesAscii } from './equation.js';
import { balance, atomTable, algebraSteps, elementsOf } from './balance.js';
import { electronBalance, lineParts, lineText } from './redox.js';
import { oxidationStates, oxText } from './oxidation.js';
import { classify } from './classify.js';
import { molarMass } from './molar.js';
import { massOf } from './elements.js';
import { CONDITIONS, molarVolume } from './stoich.js';
import { parseNum } from './format.js';
import * as STO from './ui-stoich.js';
import { computeCalc, buildEmpRows, bindEmp, empRows, setEmpRows, PANE_FIELDS, CALCS } from './ui-calc.js';
import { buildPeriodicTable, buildReference, trainerStart, trainerCheck, trainerShow, trainerRandom } from './ui-extras.js';

const MODES = ['balance', 'molar', 'stoich', 'ox', 'calc'];
const STOICH_SAMPLE = 'Zn + HCl = ZnCl2 + H2';
const PALETTE = { light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'], dark: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'] };

/* Давление своих условий по умолчанию — нормальное, и записано с десятичным
   разделителем страны («101,325» в ru, de, fr…); поле читает и точку, и запятую. */
const DEFAULT_P = F.num(CONDITIONS.stp.p);

const state = {
    mode: 'balance',
    src: { balance: '', molar: '', stoich: '', ox: '' },
    visited: {},
    mass: 'school',
    cond: 'stp', condT: '25', condP: DEFAULT_P,
    grade: 'g9',
    method: null,
    calc: 'convert',
    view: null,          // последний результат (для копирования/PDF)
    copyText: '',
    stoichPreset: null,
    auto: {},            // режим → запись подставил сам тул (пример): её результат в телеметрию не идёт
};
/** Человек что-то ввёл или выбрал сам — с этого места результат его. */
const touched = () => { state.auto[state.mode === 'calc' ? 'calc:' + state.calc : state.mode] = false; };
const isAuto = () => !!state.auto[state.mode === 'calc' ? 'calc:' + state.calc : state.mode];

/* ── окружение расчёта ───────────────────────────────────────────────────── */
function env() {
    let Vm = CONDITIONS.stp.Vm;
    let label = t('cond_stp');
    if (state.cond === 'custom') {
        const T = parseNum(state.condT);
        const P = parseNum(state.condP);
        if (isFinite(T) && T > -273.15 && P > 0) { Vm = molarVolume(T, P); label = F.num(T) + ' °C, ' + F.num(P) + ' ' + t('u_kpa'); }
    } else if (CONDITIONS[state.cond]) {
        Vm = CONDITIONS[state.cond].Vm;
        label = t('cond_' + state.cond);
    }
    // «н. у.: 0 °C, 101,3 кПа (22,4 л/моль)» → без скобки: Vm и так стоит рядом
    label = label.replace(/\s*\([^()]*\/[^()]*\)\s*$/, '');
    return { mass: state.mass, Vm: Math.round(Vm * 1000) / 1000, NA: avogadro(state.mass), condLabel: label, arrow: '=' };
}

/* ── режим ───────────────────────────────────────────────────────────────── */
function setMode(mode, opts = {}) {
    if (MODES.indexOf(mode) < 0) mode = 'balance';
    const prev = state.mode;
    if (prev !== 'calc' && $('chmInput')) state.src[prev] = $('chmInput').value;
    state.mode = mode;
    document.querySelectorAll('[data-chm-mode]').forEach((b) => {
        const on = b.getAttribute('data-chm-mode') === mode;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    const calc = mode === 'calc';
    $('chmMain').hidden = calc;
    $('chmCalcIn').hidden = !calc;
    $('chmKeysPanel').hidden = calc;
    $('chmStoichIn').hidden = mode !== 'stoich';
    const input = $('chmInput');
    const phKey = { balance: 'ph-balance', stoich: 'ph-stoich', molar: 'ph-molar', ox: 'ph-ox' }[mode];
    if (phKey) input.placeholder = input.getAttribute('data-' + phKey) || '';
    $('chmInputLabel').textContent = t(mode === 'molar' ? 'input_label_molar' : mode === 'ox' ? 'input_label_ox' : 'input_label_balance');
    document.querySelectorAll('[data-chm-for]').forEach((b) => {
        const f = b.getAttribute('data-chm-for');
        b.hidden = !!f && f.split(' ').indexOf(mode) < 0;
    });
    // примеры: реакции — в «Уравнять» и «Задаче», формулы — в «Молярной массе» и «Степенях»
    const eqMode = mode === 'balance' || mode === 'stoich';
    $('chmGrades').hidden = !eqMode;
    document.querySelectorAll('[data-chm-sample]').forEach((b) => {
        const g = b.getAttribute('data-chm-group');
        b.hidden = eqMode ? g !== state.grade : g !== mode;
    });
    if (!calc) {
        if (!opts.keep) {
            let v = state.src[mode];
            if (!v && !state.visited[mode]) {
                v = mode === 'stoich' ? STOICH_SAMPLE : mode === 'balance' ? (CFG.sample || '') : ((CFG.samples || {})[mode] || '');
                state.auto[mode] = true;
            }
            input.value = v || '';
        }
        state.visited[mode] = true;
    }
    state.method = opts.method || null;
    if (prev !== mode && !opts.silent) funnel('mode', { mode, auto: opts.auto ? 1 : 0 });
}

/** Поле следует за записью: стрелка в «Молярной массе» — значит, это реакция. */
function followInput() {
    const v = $('chmInput').value;
    if ((state.mode === 'molar' || state.mode === 'ox') && /=|->|→|⇄|⇌|⟶|<=>/.test(v)) {
        state.src[state.mode] = '';
        setMode('balance', { keep: true, auto: true });
    }
}

/* ── ошибки разбора ──────────────────────────────────────────────────────── */
function errorText(e, src) {
    if (!(e instanceof FormulaError)) return t('err_syntax');
    const info = e.info || {};
    let msg = t('err_' + e.code, { sym: info.sym || '', ch: info.ch || '' });
    if (e.at != null && src && e.code !== 'empty_right' && e.code !== 'empty_left') {
        const frag = src.slice(e.at, e.at + 3).trim();
        msg += ' (' + t('err_at', { n: e.at + 1, frag }) + ')';
    }
    return msg;
}

function showError(msg, extraHtml) {
    const box = $('chmError');
    box.innerHTML = esc(msg) + (extraHtml || '');
    box.hidden = false;
    $('chmInput').setAttribute('aria-invalid', 'true');
}
function clearError() {
    $('chmError').hidden = true;
    $('chmError').innerHTML = '';
    $('chmInput').setAttribute('aria-invalid', 'false');
}

function showResult(on) {
    $('chmCard').hidden = !on;
    $('chmSheet').hidden = !on;
    $('chmEmpty').hidden = on;
}

/* ── превью ──────────────────────────────────────────────────────────────── */
function renderPreview(html) {
    $('chmPreview').innerHTML = html || '';
}

/* ── сборка ──────────────────────────────────────────────────────────────── */
function refresh(fromInput) {
    const mode = state.mode;
    $('chmAsk').hidden = true;
    settleCancel();
    try { refreshInner(mode, fromInput); } finally { syncActions(); }
}

function refreshInner(mode, fromInput) {
    if (mode === 'calc') { renderCalc(fromInput); saveHash(); return; }
    const src = $('chmInput').value;
    state.src[mode] = src;
    if (!src.trim()) {
        clearError();
        renderPreview('');
        showResult(false);
        $('chmStRows').innerHTML = '';
        state.view = null;
        state.copyText = '';
        saveHash();
        return;
    }
    if (fromInput && !isAuto()) stage('input', { mode });
    const t0 = (window.performance && performance.now) ? performance.now() : 0;
    try {
        if (mode === 'balance' || mode === 'stoich') refreshEquation(src, fromInput, t0);
        else refreshSpecies(src, fromInput);
    } catch (e) {
        if (e instanceof FormulaError) handleParseError(e, src, fromInput);
        else {
            // не опечатка человека, а наш сбой: ярлык места — в телеметрию, фраза — человеку
            showError(t('err_syntax'));
            renderPreview(esc(src));
            showResult(false);
            caught(e, 'chm_refresh_' + mode, t('err_syntax'));
        }
    }
    saveHash();
}

/** Чипы «возможно, имелось в виду» — запись целиком встаёт в поле по нажатию. */
function maybeChips(list) {
    if (!list || !list.length) return '';
    return esc(t('v_maybe')) + ' ' + list.map((x) => '<button type="button" class="tc-chip chm-mono" dir="ltr" data-chm-set="' + esc(x) + '">' + esc(x) + '</button>').join(' ');
}
/** Ноль на месте буквы O или как «степень окисления 0» — варианты записи, которые читаются. */
function zeroFixes(src) {
    try {
        if (state.mode === 'balance' || state.mode === 'stoich') {
            // строчный ввод («h2s04 + naoh = …») — подсказка сразу в записи с регистром
            return suggestZero(src, TABLE).map((x) => {
                try { const p = parseEquation(x, TABLE); return p.notes.includes('note_case_fixed') ? canonicalEquation(p.eq) : x; } catch (_) { return x; }
            });
        }
        const one = suggestZeroSpecies(src, TABLE);
        if (!one) return [];
        try { return [speciesAscii(pickSpecies(one, TABLE).sp)]; } catch (_) { return [one]; }
    } catch (_) { return []; }
}

function handleParseError(e, src, fromInput) {
    let extra = '';
    // Одна формула во вкладке «Уравнять» — не ошибка человека, а другой вопрос.
    if (e instanceof FormulaError && e.code === 'no_arrow') {
        let isFormula = false;
        try { pickSpecies(src, TABLE); isFormula = true; } catch (_) { /* не формула */ }
        if (isFormula) {
            showError(t('err_formula_in_balance'), ' <button type="button" class="tc-chip" data-chm-go="molar">' + esc(t('go_molar')) + '</button>');
            renderPreview('');
            showResult(false);
            state.view = null;
            if (fromInput) diag('formula_in_balance', { mode: state.mode }, src);
            return;
        }
    }
    if (e instanceof FormulaError && e.code === 'unknown_element' && e.info && e.info.near && e.info.near.length) {
        extra = '<br>' + esc(t('err_unknown_near')) + ' ' + e.info.near.map((s) => '<button type="button" class="tc-chip chm-mono" data-chm-near="' + esc(s) + '" data-chm-bad="' + esc(e.info.sym) + '" data-chm-at="' + (e.at == null ? '' : e.at) + '">' + esc(s) + ' — ' + esc(elName(s)) + '</button>').join('');
    }
    // «H2S04», «Ca(0H)2», «Cu0 − 2e»: ноль вместо буквы O или как степень окисления
    let zero = [];
    if (e instanceof FormulaError && (e.code === 'zero_count' || e.code === 'stray_number')) {
        zero = zeroFixes(src);
        if (zero.length) extra += '<br>' + maybeChips(zero);
    }
    showError(errorText(e, src), extra);
    renderPreview(markAt(src, e && e.at));
    showResult(false);
    state.view = null;
    if (fromInput) {
        const code = (e && e.code) || 'unknown';
        stage('input_refused', { reason: code });
        diag('parse_' + code, { mode: state.mode, at: e && e.at != null ? e.at : -1, hint: zero.length ? 1 : 0 }, src);
    }
}

function markAt(src, at) {
    if (at == null || at < 0 || at >= src.length) return esc(src);
    return esc(src.slice(0, at)) + '<mark>' + esc(src.slice(at, at + 1)) + '</mark>' + esc(src.slice(at + 1));
}

/* ── реакция ─────────────────────────────────────────────────────────────── */
function refreshEquation(src, fromInput, t0) {
    const p = parseEquation(src, TABLE);
    clearError();
    const eq = p.eq;
    const species = eq.terms.map((x) => x.sp);
    const arrow = ARROWS[eq.arrowKind] || '=';
    renderPreview(equationHtml(eq.terms, eq.nLeft, eq.terms.map((x) => (x.coef ? fracText(x.coef) : '')), arrow));
    const r = balance(species, eq.nLeft, TABLE, { userCoefs: eq.terms.map((x) => x.coef) });
    const cls = classify(species, eq.nLeft, TABLE, { reversible: eq.arrowKind === 'reversible' });
    const eb = r.coefs && (r.verdict === 'ok') ? electronBalance(species, eq.nLeft, r.coefs, TABLE) : null;
    const view = { kind: 'balance', p, eq, species, r, cls, eb, arrow };
    state.view = view;
    if (p.question) renderQuestion(p.question, eq);
    if (state.mode === 'stoich') renderStoich(view, fromInput);
    else renderBalance(view);
    showResult(true);
    if (!fromInput || isAuto()) return;
    const ionic = species.some((x) => x.charge) ? 1 : 0;
    const half = species.some((x) => x.electron) ? 1 : 0;
    if (r.verdict === 'ok' || r.verdict === 'infinite') {
        stage('solved', { mode: state.mode, verdict: r.verdict });
        // задача считает свой ответ сама (renderStoich); здесь — «уравнено»
        if (state.mode === 'balance') {
            complete('balance|' + src, {
                mode: 'balance', kind: half ? 'half' : (cls.redox ? 'redox' : 'plain'), verdict: r.verdict, n: species.length, ionic,
                eb: eb && eb.ok ? 1 : 0, resolved: r.resolved ? 1 : 0, q: p.question ? 1 : 0,
                cf: p.notes.includes('note_case_fixed') ? 1 : 0, tag: eq.terms.some((x) => x.tag) ? 1 : 0,
                ms: t0 ? Math.round(performance.now() - t0) : 0,
            }, () => recentAdd('balance', src));
            // «не умею»: ОВР есть, а электронный баланс школьной записью не строится
            if (eb && !eb.ok && cls.redox && eb.reason !== 'half_reaction' && eb.reason !== 'not_redox') diag('eb_' + eb.reason, { mode: 'balance', n: species.length, ionic }, src);
            else if (r.verdict === 'infinite') diag('balance_infinite', { mode: 'balance', n: species.length, ionic }, src);
        }
    } else {
        stage('input_refused', { reason: 'balance_' + r.verdict });
        diag('balance_' + r.verdict, { mode: state.mode, n: species.length, ionic, why: r.why || '' }, src);
    }
}

const fracText = (f) => (f.d === 1n ? f.n.toString() : f.n.toString() + '/' + f.d.toString());

/** Вопрос «CO — угарный газ или Co — кобальт?» с вариантами. */
function renderQuestion(q, eq) {
    const box = $('chmAsk');
    const opt = (r) => {
        const els = Object.keys(r.counts).map((el) => el + ' — ' + elName(el)).join(LIST_COMMA);
        return '<button type="button" class="tc-chip" data-chm-pick="' + esc(r.formula) + '" data-chm-charge="' + r.charge + '"><b dir="ltr">' + speciesHtml(r) + '</b>&nbsp;<span>(' + esc(els) + ')</span></button>';
    };
    box.innerHTML = '<b>' + esc(t('ask_case', { x: q.raw })) + '</b><br><span class="chm-note">' + esc(t('ask_hint', { f: speciesText(q.options[0]) })) + '</span>'
        + '<div class="chm-ask-opts">' + q.options.map(opt).join('') + '</div>';
    box.hidden = false;
    box.dataset.term = String(q.term);
    state.askEq = eq;
    ae('ask_case', { n: q.options.length });
}

/** Человек выбрал прочтение — переписываем именно это вещество в поле. */
function applyPick(formula, charge) {
    const input = $('chmInput');
    const sp = { formula, charge: +charge, electron: false };
    if (state.mode === 'molar' || state.mode === 'ox') {
        input.value = speciesAscii(sp);
    } else if (state.askEq) {
        const ti = +$('chmAsk').dataset.term;
        const tm = state.askEq.terms[ti];
        const src = normalizeChars(input.value);
        const lc = leadingCoef(tm.raw);
        const coef = /^\s*(\d+(?:[.,]\d+)?(?:\/\d+)?)/.exec(tm.raw);
        const suffix = (/(\s*\(\s*[a-zа-яёіїєґ.\-‑ ]{1,14}\s*\)|\s*[↑↓])+$/i.exec(lc.rest) || [''])[0];
        const repl = (coef ? coef[1] : '') + speciesAscii(sp) + suffix;
        const at = src.indexOf(tm.raw, Math.max(0, tm.at - 1));
        if (at >= 0) input.value = src.slice(0, at) + repl + src.slice(at + tm.raw.length);
    }
    ae('ask_pick', {});
    refresh(true);
}

function sumsHtml(eq, coefs) {
    const L = coefs.slice(0, eq.nLeft).reduce((s, x) => s + Number(x), 0);
    const R = coefs.slice(eq.nLeft).reduce((s, x) => s + Number(x), 0);
    return '<div class="chm-facts"><span class="chm-fact">' + esc(t('sum_left')) + ' <b>' + L + '</b></span><span class="chm-fact">' + esc(t('sum_right')) + ' <b>' + R + '</b></span><span class="chm-fact">' + esc(t('sum_total')) + ' <b>' + (L + R) + '</b></span></div>';
}

function tagsHtml(cls) {
    if (!cls.tags.length) return '';
    return '<div class="chm-facts">' + cls.tags.map((k) => '<span class="chm-fact">' + esc(t(k)) + '</span>').join('') + '</div>';
}

function notesHtml(p, view) {
    let h = '';
    if (p.notes.includes('note_case_fixed')) {
        const canon = canonicalEquation(view.eq);
        h += '<p class="chm-note">' + esc(t('note_case_fixed', { eq: canon })) + ' <button type="button" class="tc-chip" data-chm-set="' + esc(canon) + '">' + esc(t('fix_in_field')) + '</button></p>';
    }
    if (p.notes.includes('note_homoglyphs')) h += '<p class="chm-note">' + esc(t('note_homoglyphs')) + '</p>';
    return h;
}

/** Каноническая ASCII-запись с коэффициентами человека — для кнопки «исправить в поле». */
function canonicalEquation(eq) {
    const part = (arr) => arr.map((tm) => {
        const coef = /^\s*(\d+(?:[.,]\d+)?(?:\/\d+)?)/.exec(tm.raw);
        return (coef ? coef[1] + ' ' : '') + speciesAscii(tm.sp) + (tm.state ? '(' + tm.state + ')' : '') + (tm.tag ? '(' + tm.tag + ')' : '') + (tm.mark || '');
    }).join(' + ');
    const arrow = eq.arrowKind === 'reversible' ? '⇄' : eq.arrowKind === 'arrow' ? '->' : '=';
    return part(eq.terms.slice(0, eq.nLeft)) + ' ' + arrow + ' ' + part(eq.terms.slice(eq.nLeft));
}

function renderBalance(view) {
    const { eq, r, cls, p, arrow, species } = view;
    const card = [];
    const copy = [];
    let answerCoefs = null;
    const verdict = (cls2, text) => card.push('<p class="chm-verdict ' + cls2 + '">' + esc(text) + '</p>');
    const names = (idx) => idx.map((i) => speciesText(species[i])).join(', ');

    if (r.verdict === 'ok' || r.verdict === 'infinite') answerCoefs = r.coefs.map(String);
    if (answerCoefs) {
        card.push('<p class="chm-ans-l">' + esc(t('ans_balanced')) + '</p>');
        card.push('<p class="chm-ans" dir="ltr">' + equationHtml(eq.terms, eq.nLeft, answerCoefs, arrow) + '</p>');
        copy.push(eqTextU(eq.terms, eq.nLeft, answerCoefs, arrow));
    }
    if (r.verdict === 'ok') {
        const u = r.user;
        if (u && u.balanced && u.fractional) verdict('good', t('v_fractional'));
        else if (u && u.balanced && u.reducible) verdict('warn', t('v_reducible', { k: u.factor.toString() }));
        else if (u && u.balanced && u.sameAsOurs !== false) verdict('good', t('v_already'));
        else if (u && !u.balanced) verdict('warn', t('v_user_wrong'));
        else verdict('good', t('v_ok'));
        if (r.resolved) card.push('<p class="chm-note">' + esc(t('v_resolved', { s: eqTextU(eq.terms, eq.nLeft, r.resolved.simplest.map(String), arrow) })) + '</p>');
        card.push(sumsHtml(eq, r.coefs));
    } else if (r.verdict === 'missing') {
        for (const m of r.missing) verdict('warn', t(m.side === 'right' ? 'v_missing_right' : 'v_missing_left', { el: m.el, name: elName(m.el) }));
        let sug = null;
        try { sug = suggestCase(eq, TABLE); } catch (_) { sug = null; }
        const fixes = (sug ? [sug] : []).concat(zeroFixes($('chmInput').value));
        if (fixes.length) card.push('<p class="chm-note">' + maybeChips(fixes) + '</p>');
        copy.push(eqTextU(eq.terms, eq.nLeft, null, arrow), r.missing.map((m) => t(m.side === 'right' ? 'v_missing_right' : 'v_missing_left', { el: m.el, name: elName(m.el) })).join(' '));
    } else if (r.verdict === 'impossible') {
        verdict('warn', t(r.why === 'charge_or_atoms' ? 'v_impossible_charge' : 'v_impossible'));
        let sug = null;
        try { sug = suggestCase(eq, TABLE); } catch (_) { sug = null; }
        const fixes = (sug ? [sug] : []).concat(zeroFixes($('chmInput').value));
        if (fixes.length) card.push('<p class="chm-note">' + maybeChips(fixes) + '</p>');
        copy.push(eqTextU(eq.terms, eq.nLeft, null, arrow), t(r.why === 'charge_or_atoms' ? 'v_impossible_charge' : 'v_impossible'));
    } else if (r.verdict === 'zero') {
        const keep = eq.terms.map((_, i) => i).filter((i) => r.zero.indexOf(i) < 0);
        const terms2 = keep.map((i) => eq.terms[i]);
        const nL2 = keep.filter((i) => i < eq.nLeft).length;
        const c2 = keep.map((i) => String(r.coefs[i]));
        verdict('warn', t('v_zero', { x: names(r.zero) }));
        card.push('<p class="chm-ans" dir="ltr">' + equationHtml(terms2, nL2, c2, arrow) + '</p>');
        copy.push(eqTextU(terms2, nL2, c2, arrow), t('v_zero', { x: names(r.zero) }));
    } else if (r.verdict === 'wrong_side') {
        verdict('warn', t('v_wrong_side', { x: names(r.wrong) }));
        const moved = movedEquation(eq, r.wrong);
        card.push('<p class="chm-note"><button type="button" class="tc-chip chm-mono" dir="ltr" data-chm-set="' + esc(moved) + '">' + esc(t('v_move')) + LABEL_SEP + esc(moved) + '</button></p>');
        copy.push(eqTextU(eq.terms, eq.nLeft, null, arrow), t('v_wrong_side', { x: names(r.wrong) }));
    }
    if (r.verdict === 'infinite') {
        verdict('warn', t('v_infinite'));
        card.push('<p class="chm-note">' + esc(t('v_infinite_simplest')) + '</p>');
        card.push(sumsHtml(eq, r.coefs));
    }
    if ((r.verdict === 'ok' || r.verdict === 'infinite') && species.some(oddSimple)) {
        const fixes = zeroFixes($('chmInput').value);
        if (fixes.length) card.push('<p class="chm-note">' + maybeChips(fixes) + '</p>');
    }
    card.push(tagsHtml(cls));
    card.push(notesHtml(p, view));
    $('chmCardBody').innerHTML = card.join('');
    flash($('chmCardBody'));

    // Лист для тетради
    const methods = [];
    if (answerCoefs && r.verdict === 'ok') {
        if (view.eb && view.eb.ok) methods.push('ebalance');
        methods.push('table', 'algebra');
    }
    renderMethods(methods);
    const m = state.method && methods.indexOf(state.method) >= 0 ? state.method : methods[0];
    state.method = m || null;
    $('chmSheetTitle').textContent = t('sheet_balance');
    let body = '';
    let text = '';
    if (!methods.length) {
        const res = sheetProblems(view);
        body = res.html;
        text = res.text;
    } else if (m === 'ebalance') ({ html: body, text } = sheetEbalance(view));
    else if (m === 'algebra') ({ html: body, text } = sheetAlgebra(view));
    else ({ html: body, text } = sheetTable(view));
    // Вкладки «Электронный баланс» нет — сказать почему: за ней сюда и приходят.
    const why = ebWhy(view);
    if (why && methods.length) {
        const line = '<p class="chm-why chm-ebwhy">' + esc(t('method_ebalance')) + esc(LABEL_SEP) + esc(why.text) + '</p>';
        body = why.top ? line + body : body + line;
    }
    $('chmSheetBody').innerHTML = body;
    state.copyText = copy.concat(text ? ['', text] : []).join('\n');
}

/** Причина, по которой электронного баланса нет: «не ОВР» и «полуреакция» — справка (внизу
 *  листа), «не строится» — отказ (вверху, его ищут). */
function ebWhy(view) {
    const eb = view.eb;
    if (!eb || eb.ok || !eb.reason) return null;
    const key = { not_redox: 'eb_not_redox', half_reaction: 'eb_half', complex: 'eb_complex', ambiguous_ox: 'eb_ambiguous', mismatch: 'eb_mismatch' }[eb.reason];
    if (!key) return null;
    return { text: t(key), top: eb.reason !== 'not_redox' && eb.reason !== 'half_reaction' };
}

function movedEquation(eq, wrong) {
    const L = [];
    const R = [];
    eq.terms.forEach((tm, i) => {
        const left = i < eq.nLeft;
        const flip = wrong.indexOf(i) >= 0;
        ((left !== flip) ? L : R).push(speciesAscii(tm.sp));
    });
    return L.join(' + ') + ' = ' + R.join(' + ');
}

function renderMethods(methods) {
    const bar = $('chmMethods');
    if (methods.length < 2) { bar.hidden = true; bar.innerHTML = ''; return; }
    const cur = state.method && methods.indexOf(state.method) >= 0 ? state.method : methods[0];
    bar.innerHTML = methods.map((m) => '<button type="button" class="tc-seg-b' + (m === cur ? ' on' : '') + '" role="tab" aria-selected="' + (m === cur) + '" data-chm-method="' + m + '">' + esc(t('method_' + m)) + '</button>').join('');
    bar.hidden = false;
}

function atomTableHtml(rows, after) {
    return '<div class="chm-wrap"><table class="chm-table"><thead><tr><th>' + esc(t('tbl_element')) + '</th><th>' + esc(t('tbl_left')) + '</th><th>' + esc(t('tbl_right')) + '</th>' + (after ? '<th></th>' : '') + '</tr></thead><tbody>'
        + rows.map((r) => {
            const ok = r.left === r.right;
            const label = r.el === 'charge' ? t('tbl_charge') : r.el;
            const fmt = (v) => (r.el === 'charge' ? (v > 0 ? '+' + v : String(v).replace('-', '−')) : String(v));
            return '<tr' + (!ok ? ' class="bad"' : '') + '><td>' + esc(label) + '</td><td>' + esc(fmt(r.left)) + '</td><td>' + esc(fmt(r.right)) + '</td>' + (after ? '<td class="' + (ok ? 'ok">✓' : 'no">✗') + '</td>' : '') + '</tr>';
        }).join('') + '</tbody></table></div>';
}
function atomTableText(rows) {
    return rows.map((r) => (r.el === 'charge' ? t('tbl_charge') : r.el) + LABEL_SEP + r.left + ' | ' + r.right + (r.left === r.right ? ' ✓' : ' ✗')).join('\n');
}

function schemeHtml(view) {
    const { eq, arrow } = view;
    const userCoefs = eq.terms.map((x) => (x.coef ? fracText(x.coef) : ''));
    return '<p class="chm-h">' + esc(t('h_task')) + '</p><p class="chm-line big" dir="ltr">' + equationHtml(eq.terms, eq.nLeft, userCoefs, arrow) + '</p>';
}
function answerHtml(view) {
    const { eq, r, arrow } = view;
    return '<p class="chm-answer" dir="ltr">' + esc(t('h_answer')) + ' ' + equationHtml(eq.terms, eq.nLeft, r.coefs.map(String), arrow) + '</p>';
}

function sheetTable(view) {
    const { eq, species, r, arrow } = view;
    const before = eq.terms.map((x) => (x.coef ? Number(x.coef.n) / Number(x.coef.d) : 1));
    const rowsBefore = atomTable(species, eq.nLeft, before.map((x) => (Number.isInteger(x) ? BigInt(x) : 1n)));
    const rowsAfter = atomTable(species, eq.nLeft, r.coefs);
    let h = schemeHtml(view);
    h += '<p class="chm-h">' + esc(t('h_count_before')) + '</p>' + atomTableHtml(rowsBefore, true);
    const bad = rowsBefore.filter((x) => x.left !== x.right).map((x) => (x.el === 'charge' ? t('tbl_charge') : x.el));
    h += '<p class="chm-why">' + esc(bad.length ? t('why_table_fix', { els: bad.join(', ') }) : t('why_table')) + '</p>';
    h += '<p class="chm-h">' + esc(t('h_count_after')) + '</p><p class="chm-line" dir="ltr">' + equationHtml(eq.terms, eq.nLeft, r.coefs.map(String), arrow) + '</p>' + atomTableHtml(rowsAfter, true);
    h += '<p class="chm-why">' + esc(t('check_ok')) + '</p>' + answerHtml(view);
    const text = [t('h_count_before'), atomTableText(rowsBefore), '', t('h_count_after'), atomTableText(rowsAfter)].join('\n');
    return { html: h, text };
}

function sheetAlgebra(view) {
    const { eq, species, r } = view;
    const a = algebraSteps(species, eq.nLeft, r.coefs);
    const lettered = eq.terms.map((x, i) => a.letters[i] + '·' + speciesText(x.sp));
    const scheme = lettered.slice(0, eq.nLeft).join(' + ') + ' = ' + lettered.slice(eq.nLeft).join(' + ');
    let h = schemeHtml(view);
    h += '<p class="chm-h">' + esc(t('alg_intro')) + '</p><p class="chm-line" dir="ltr">' + esc(scheme) + '</p>';
    h += '<p class="chm-h">' + esc(t('alg_equations')) + '</p>';
    h += a.equations.map((e) => '<p class="chm-line" dir="ltr"><b>' + esc(e.el === 'charge' ? t('tbl_charge') : e.el) + ':</b> ' + esc(e.text) + '</p>').join('');
    h += '<p class="chm-h">' + esc(t('alg_result')) + '</p><ol class="chm-steps">';
    const lines = [];
    for (const s of a.steps) {
        const why = t(s.key, { el: s.el === 'charge' ? t('tbl_charge') : (s.el || ''), k: s.k || '' });
        h += '<li><span class="chm-why">' + esc(why) + '</span><span class="chm-math" dir="ltr">' + esc(s.text) + '</span></li>';
        lines.push(why + ' ' + s.text);
    }
    h += '</ol>' + answerHtml(view);
    const text = [t('alg_intro'), scheme, '', t('alg_equations')].concat(a.equations.map((e) => (e.el === 'charge' ? t('tbl_charge') : e.el) + LABEL_SEP + e.text)).concat(['', t('alg_result')]).concat(lines).join('\n');
    return { html: h, text };
}

function sheetEbalance(view) {
    const { eq, species, r, eb, arrow } = view;
    // какие узлы меняют степень — подсветить
    const changedEls = new Set(eb.lines.map((ln) => ln.el));
    const changedKeys = new Set();
    eb.lines.forEach((ln) => { changedKeys.add(ln.el + '|' + ln.from.n + '/' + ln.from.d + '|L'); changedKeys.add(ln.el + '|' + ln.to.n + '/' + ln.to.d + '|R'); });
    const speciesOpts = (j) => {
        const ox = eb.ox[j];
        const changed = new Set();
        for (const a of (ox.atoms || [])) {
            const v = ox.byId[a.id];
            if (!v) continue;
            const side = j < eq.nLeft ? 'L' : 'R';
            if (changedKeys.has(a.el + '|' + v.n + '/' + v.d + '|' + side)) changed.add(a.id);
        }
        return { ox, changed, all: true };
    };
    let h = schemeHtml(view);
    h += '<p class="chm-h">' + esc(t('eb_states')) + '</p>';
    h += '<p class="chm-oxeq" dir="ltr">' + equationHtml(eq.terms, eq.nLeft, null, arrow, { speciesOpts, noState: true }) + '</p>';
    const shifts = eb.lines.map((ln) => ln.el + ' ' + oxShow(oxText(ln.from)) + ' → ' + oxShow(oxText(ln.to)));
    h += '<p class="chm-why">' + esc(t('eb_states_why', { list: Array.from(new Set(shifts)).join(LIST_SEP) })) + '</p>';

    h += '<p class="chm-h">' + esc(t('eb_lines')) + '</p><div class="chm-wrap"><div class="chm-eb" dir="ltr">';
    const allLines = eb.groups.flatMap((g) => g.lines.map((ln) => ({ ln, g })));
    const textLines = [];
    allLines.forEach(({ ln, g }, i) => {
        const pp = lineParts(ln);
        const part = (x) => esc(x.n) + esc(x.el) + (x.sub ? '<sub>' + esc(x.sub) + '</sub>' : '') + '<sup>' + esc(oxShow(x.ox)) + '</sup>';
        const line = part(pp.left) + (pp.dir === 'gain' ? ' + ' : ' − ') + esc(pp.e) + 'e<sup>−</sup> → ' + part(pp.right);
        h += '<span class="chm-eb-l">' + line + '</span>';
        if (i === 0) h += eb.lcm ? '<span class="chm-eb-lcm" style="grid-row:span ' + allLines.length + '">' + esc(eb.lcm.toString()) + '</span>' : '<span class="chm-eb-lcm" style="grid-row:span ' + allLines.length + '"></span>';
        h += '<span class="chm-eb-m">' + esc(g.mult.toString()) + '</span>';
        textLines.push(lineText(ln, oxSupText, subDigits) + '  | ' + g.mult);
    });
    h += '</div></div>';
    if (eb.lcm) h += '<p class="chm-why">' + esc(t('eb_lcm', { k: eb.lcm.toString() })) + '</p>';

    // Роли. Степень у символа: на листе — надстрочно (Mn⁺⁷ / Mn^+VII), в тексте для
    // копирования — «Mn⁺⁷» или «Mn +VII» (римских надстрочных цифр в Юникоде нет,
    // а «(Mn(+VII))» в скобках роли читается хуже).
    const roleText = [];
    const roleOf = (idx, key, proc) => idx.map((j) => {
        const els = eb.lines.filter((ln) => ln.fromCell.per.has(j) && ((key === 'eb_oxidizer') === (ln.dir === 'gain')));
        const seen = new Set();
        const uniq = els.filter((ln) => { const k = ln.el + '|' + oxText(ln.from); if (seen.has(k)) return false; seen.add(k); return true; });
        const stText = uniq.map((ln) => ln.el + (OX_NOTATION === 'roman' ? ' ' + oxShow(oxText(ln.from)) : oxSupText(oxText(ln.from)))).join(', ');
        const stHtml = uniq.map((ln) => esc(ln.el) + '<sup>' + esc(oxShow(oxText(ln.from))) + '</sup>').join(', ');
        roleText.push(speciesText(species[j]) + ' (' + stText + ') — ' + t(key) + LIST_COMMA + t(proc));
        return '<p><b dir="ltr">' + speciesHtml(species[j]) + '</b> (<span dir="ltr">' + stHtml + '</span>) — ' + esc(t(key)) + esc(LIST_COMMA) + esc(t(proc)) + '</p>';
    }).join('');
    const both = eb.oxidizers.filter((j) => eb.reducers.indexOf(j) >= 0);
    h += '<div class="chm-roles">' + roleOf(eb.oxidizers, 'eb_oxidizer', 'eb_reduction') + roleOf(eb.reducers, 'eb_reducer', 'eb_oxidation') + '</div>';
    if (both.length) h += '<p class="chm-why">' + esc(t('eb_disprop')) + '</p>';

    // Перенос множителей и остальные элементы
    const involved = new Set();
    eb.lines.forEach((ln) => { for (const j of ln.fromCell.per.keys()) involved.add(j); for (const j of ln.toCell.per.keys()) involved.add(j); });
    // Вещество, у которого часть атомов степень не меняет (HCl: хлор идёт и в Cl₂, и в соли):
    // его коэффициент из множителей баланса не следует — «HCl — 16» рядом с множителями 2 и 5
    // выглядело взятым с потолка. Такие идут отдельной строкой с объяснением.
    const partial = new Map();
    eb.lines.forEach((ln) => {
        if (ln.partialFrom) for (const j of ln.fromCell.per.keys()) if (j < eq.nLeft) partial.set(j, ln.el);
        if (ln.partialTo) for (const j of ln.toCell.per.keys()) if (j >= eq.nLeft) partial.set(j, ln.el);
    });
    const placed = eq.terms.map((_, j) => j).filter((j) => involved.has(j) && !partial.has(j)).map((j) => speciesText(species[j]) + ' — ' + r.coefs[j]);
    h += '<p class="chm-h">' + esc(t('eb_place')) + '</p>';
    if (placed.length) h += '<p class="chm-line" dir="ltr">' + esc(placed.join(LIST_SEP)) + '</p>';
    const partialText = [];
    partial.forEach((el, j) => {
        const line = t('eb_partial', { x: speciesText(species[j]), el, k: r.coefs[j].toString() });
        partialText.push(line);
        h += '<p class="chm-why">' + esc(line) + '</p>';
    });
    const rest = elementsOf(species).filter((el) => !changedEls.has(el));
    if (rest.length) h += '<p class="chm-why">' + esc(t('eb_rest', { els: rest.join(', ') })) + '</p>';
    if (eb.average) h += '<p class="chm-why">' + esc(t('eb_average')) + '</p>';
    if (eb.organic) h += '<p class="chm-why">' + esc(t('eb_organic')) + '</p>';
    h += '<p class="chm-h">' + esc(t('h_check')) + '</p>' + atomTableHtml(atomTable(species, eq.nLeft, r.coefs), true);
    h += answerHtml(view);
    const text = [t('eb_lines')].concat(textLines).concat(eb.lcm ? [t('eb_lcm', { k: eb.lcm.toString() })] : []).concat(roleText).concat(partialText).join('\n');
    return { html: h, text };
}

/** Лист, когда уравнять нельзя: схема + счёт атомов показывают, ГДЕ не сходится. */
function sheetProblems(view) {
    const { eq, species, r } = view;
    let h = schemeHtml(view);
    const ones = eq.terms.map(() => 1n);
    const coefs = r.coefs || ones;
    h += '<p class="chm-h">' + esc(t('h_count_before')) + '</p>' + atomTableHtml(atomTable(species, eq.nLeft, r.verdict === 'missing' || r.verdict === 'impossible' ? ones : coefs), true);
    if (r.verdict === 'infinite' && r.rays && r.rays.length) {
        h += '<p class="chm-h">' + esc(t('v_processes')) + '</p>';
        for (const ray of r.rays.slice(0, 6)) {
            const idx = ray.map((x, i) => (x > 0n ? i : -1)).filter((i) => i >= 0);
            const terms = idx.map((i) => eq.terms[i]);
            const nL = idx.filter((i) => i < eq.nLeft).length;
            h += '<p class="chm-line" dir="ltr">' + equationHtml(terms, nL, idx.map((i) => String(ray[i])), view.arrow) + '</p>';
        }
        h += '<p class="chm-why">' + esc(t('v_infinite_simplest')) + '</p><p class="chm-answer" dir="ltr">' + equationHtml(eq.terms, eq.nLeft, r.coefs.map(String), view.arrow) + '</p>';
    }
    return { html: h, text: '' };
}

/* ── задача по уравнению ─────────────────────────────────────────────────── */
function renderStoich(view, fromInput) {
    const { eq, r, arrow } = view;
    if (r.verdict !== 'ok') {
        renderBalance(view);
        $('chmStRows').innerHTML = '';
        $('chmStMsg').hidden = false;
        $('chmStMsg').textContent = t('st_need_balance');
        return;
    }
    const preset = state.stoichPreset;
    state.stoichPreset = null;
    let rows = preset;
    if (!rows && STO.ST.key === '' && eq.terms.map((x) => x.sp.formula).join() === 'Zn,HCl,ZnCl2,H2') rows = STO.demoRows(eq);
    STO.syncRows(eq, rows);
    const e = env();
    e.arrow = arrow;
    const res = STO.solveCurrent(eq, r.coefs, e);
    const msg = $('chmStMsg');
    const bal = '<p class="chm-ans-l">' + esc(t('ans_balanced')) + '</p><p class="chm-ans" dir="ltr">' + equationHtml(eq.terms, eq.nLeft, r.coefs.map(String), arrow) + '</p>';
    $('chmSheetTitle').textContent = t('sheet_stoich');
    renderMethods([]);
    if (res.status === 'ok') {
        msg.hidden = true;
        $('chmCardBody').innerHTML = res.cardHtml + bal;
        $('chmSheetBody').innerHTML = res.sheetHtml;
        state.copyText = res.text;
        flash($('chmCardBody'));
        if (fromInput && !isAuto()) {
            const rr = res.res;
            complete('stoich|' + $('chmInput').value + '|' + rr.answers.map((a) => a.text).join(), {
                mode: 'stoich', kind: STO.taskKind(), n: rr.answers.length, lim: rr.limiting ? (rr.limiting.tie ? 2 : 1) : 0,
                eta: STO.ST.eta ? 1 : 0, gas: rr.dano.some((d) => /^Vm/.test(d)) ? state.cond : '', species: eq.terms.length,
            }, () => recentAdd('stoich', $('chmInput').value));
        }
    } else {
        msg.hidden = false;
        msg.textContent = res.msg;
        $('chmCardBody').innerHTML = bal + '<p class="chm-verdict">' + esc(res.msg) + '</p>';
        $('chmSheetBody').innerHTML = '<p class="chm-line big" dir="ltr">' + equationHtml(eq.terms, eq.nLeft, r.coefs.map(String), arrow) + '</p><p class="chm-why">' + esc(res.msg) + '</p>';
        state.copyText = eqTextU(eq.terms, eq.nLeft, r.coefs.map(String), arrow);
        // незаполненное условие — не отказ; отказ — число, которое не прочиталось, или процент вне 0–100
        if (fromInput && !isAuto() && res.status === 'error') diag('stoich_' + (res.code || 'error'), { mode: 'stoich', kind: STO.taskKind() }, res.bad || '');
    }
}

/* ── одно вещество: молярная масса и степени окисления ───────────────────── */
function refreshSpecies(src, fromInput) {
    const p = pickSpecies(src, TABLE);
    clearError();
    renderPreview(speciesHtml(p.sp));
    if (p.question) renderQuestion(p.question, null);
    if (p.sp.electron) throw new FormulaError('syntax', 0);
    if (state.mode === 'molar') renderMolar(p);
    else renderOx(p);
    showResult(true);
    if (!fromInput || isAuto()) return;
    stage('solved', { mode: state.mode });
    const sp = p.sp;
    const base = { n: Object.keys(sp.counts).length, q: p.question ? 1 : 0, cf: p.notes.includes('note_case_fixed') ? 1 : 0 };
    if (state.mode === 'molar') {
        complete('molar|' + src + '|' + state.mass, Object.assign({ mode: 'molar', kind: sp.units.length > 1 ? 'hydrate' : (sp.charge ? 'ion' : 'plain'), mass: state.mass }, base), () => recentAdd('molar', src));
    } else {
        const ox = state.oxLast || {};
        // «не определяются» — честный отказ: его и считаем отказом, а не ответом
        if (ox.status === 'ambiguous') diag(ox.note || 'ox_note_unsolved', { mode: 'ox' }, src);
        else complete('ox|' + src, Object.assign({ mode: 'ox', kind: ox.status === 'exact' ? (sp.charge ? 'ion' : 'exact') : (ox.organic ? 'organic' : 'average') }, base), () => recentAdd('ox', src));
    }
}

function themeDark() {
    const th = document.documentElement.getAttribute('data-theme');
    if (th === 'light') return false;
    if (th === 'dark') return true;
    try { return !window.matchMedia || !window.matchMedia('(prefers-color-scheme: light)').matches; } catch (_) { return true; }
}

function renderMolar(p) {
    const sp = p.sp;
    const mm = molarMass(sp, TABLE, state.mass);
    const U = units();
    const X = speciesText(sp);
    const Ms = F.num(mm.M);
    const sym = mm.parts.map((q) => (q.n > 1 ? q.n + '·' : '') + 'Ar(' + q.el + ')').join(' + ');
    const nums = mm.parts.map((q) => (q.n > 1 ? q.n + '·' : '') + F.num(q.ar)).join(' + ');
    const line = t('mm_line_sym', { f: X, sym, num: nums, val: Ms });
    const pal = PALETTE[themeDark() ? 'dark' : 'light'];
    let card = '<p class="chm-ans-l">' + esc(t('mm_ans')) + '</p><p class="chm-big" dir="ltr">M(' + speciesHtml(sp) + ') = <span class="chm-f">' + esc(Ms) + '&nbsp;' + esc(U.gmol) + '</span></p>';
    card += '<div class="chm-facts"><span class="chm-fact">' + esc(t('mm_atoms')) + ' <b>' + mm.atoms + '</b></span><span class="chm-fact">' + esc(t(state.mass === 'precise' ? 'mm_precise' : 'mm_school')) + '</span>';
    // Доля воды — записью страны (ω(H₂O) в ru, w(H₂O) в de/fr, %H₂O в en/es/pt); число — жирным.
    if (mm.hydrate && mm.hydrate.water) {
        const wv = '<b>' + esc(F.pct(mm.hydrate.water.w * 100)) + '</b>';
        card += '<span class="chm-fact" dir="ltr">' + t('mm_water_fact').split(':w').map(esc).join(wv) + '</span>';
    }
    card += '</div>' + notesHtmlSpecies(p);
    $('chmCardBody').innerHTML = card;
    flash($('chmCardBody'));

    let h = '<p class="chm-h">' + esc(t('mm_h_break')) + '</p><p class="chm-line big" dir="ltr">' + esc(line) + '</p>';
    // Вторая строка — от числа к граммам: «M(H₂SO₄) = 98 г/моль» после безразмерной Mr
    // (ru) или «1 mol of H₂SO₄ has a mass of 98 g» (остальные). Есть во всех локалях.
    const unitsLine = t('mm_units', { f: X, val: Ms });
    h += '<p class="chm-line" dir="ltr">' + esc(unitsLine) + '</p>';
    const text = [line, unitsLine];
    if (mm.hydrate && mm.hydrate.length > 1) {
        const parts = mm.hydrate.map((u) => (u.mult > 1 ? u.mult + '·' : '') + 'M(' + formulaUnicode(u.text, 0) + ')').join(' + ');
        const partNums = mm.hydrate.map((u) => (u.mult > 1 ? u.mult + '·' : '') + F.num(Math.round(u.M * 1000) / 1000)).join(' + ');
        const hl = t('mm_hydrate', { f: X, parts, nums: partNums, val: Ms });
        h += '<p class="chm-line" dir="ltr">' + esc(hl) + '</p>';
        text.push(hl);
        if (mm.hydrate.water) {
            const wl = t('mm_water', { mw: F.num(Math.round(mm.hydrate.water.mult * mm.hydrate.water.M * 1000) / 1000), M: Ms, w: F.pct(mm.hydrate.water.w * 100) });
            h += '<p class="chm-line" dir="ltr">' + esc(wl) + '</p>';
            text.push(wl);
        }
    }
    // Состав: полоса 100 % + таблица (она же легенда и табличный вид).
    h += '<p class="chm-h">' + esc(t('mm_h_fractions')) + '</p>';
    h += '<div class="chm-bar" role="img" aria-label="' + esc(t('mm_bar_label', { f: X })) + '">' + mm.parts.map((q, i) => '<i style="flex:' + (q.w * 1000).toFixed(1) + ' 1 0;background:' + pal[i % pal.length] + '" title="' + esc(q.el + LABEL_SEP + F.pct(q.w * 100) + ' %') + '"></i>').join('') + '</div>';
    h += '<div class="chm-wrap"><table class="chm-table"><thead><tr><th>' + esc(t('mm_col_el')) + '</th><th>' + esc(t('mm_col_n')) + '</th><th>' + esc(t('mm_col_ar')) + '</th><th>' + esc(t('mm_col_contrib')) + '</th><th>' + esc(t('mm_col_w')) + '</th></tr></thead><tbody>'
        + mm.parts.map((q, i) => '<tr><td><span class="sw" style="background:' + pal[i % pal.length] + '"></span>' + esc(q.el) + ' <span class="chm-u">' + esc(elName(q.el)) + '</span></td><td>' + q.n + '</td><td>' + esc(F.num(q.ar)) + '</td><td>' + esc(F.num(Math.round(q.contrib * 1000) / 1000)) + '</td><td>' + esc(F.pct(q.w * 100)) + '</td></tr>').join('')
        + '</tbody></table></div>';
    const wl = mm.parts.map((q) => t('mm_w_line', { el: q.el, n: q.n > 1 ? q.n + '·' : '', ar: F.num(q.ar), M: Ms, w: F.val(q.w), pct: F.pct(q.w * 100) }));
    h += '<ol class="chm-steps">' + wl.map((l) => '<li><span class="chm-math" dir="ltr">' + esc(l) + '</span></li>').join('') + '</ol>';
    h += '<p class="chm-why">' + esc(t('mm_used', { list: mm.parts.map((q) => q.el + ' = ' + F.num(q.ar)).join(', ') })) + '</p>';
    h += '<p class="chm-why"><button type="button" class="tc-chip" data-chm-convert="' + esc(speciesAscii(sp)) + '">' + esc(t('mm_to_convert', { f: X })) + '</button></p>';
    $('chmSheetTitle').textContent = t('sheet_molar');
    renderMethods([]);
    $('chmSheetBody').innerHTML = h;
    state.copyText = text.concat(wl).join('\n');
}

function notesHtmlSpecies(p) {
    let h = '';
    // «Na20», «H20»: двадцать атомов одного элемента — почти наверняка ноль на месте буквы O
    if (oddSimple(p.sp)) {
        const fixes = zeroFixes($('chmInput').value);
        if (fixes.length) h += '<p class="chm-note">' + maybeChips(fixes) + '</p>';
    }
    if (p.notes.includes('note_case_fixed')) h += '<p class="chm-note">' + esc(t('note_now_as', { f: speciesText(p.sp) })) + ' <button type="button" class="tc-chip" data-chm-set="' + esc(speciesAscii(p.sp)) + '">' + esc(t('fix_in_field')) + '</button></p>';
    if (p.notes.includes('note_homoglyphs')) h += '<p class="chm-note">' + esc(t('note_homoglyphs')) + '</p>';
    return h;
}

function renderOx(p) {
    const sp = p.sp;
    const ox = oxidationStates(sp, TABLE);
    state.oxLast = { status: ox.status, note: ox.note, organic: ox.organic };
    const big = '<span class="chm-oxbig" dir="ltr">' + speciesHtml(sp, { ox, all: true }) + '</span>';
    const statusKey = ox.status === 'exact' ? 'ox_status_exact' : ox.note || 'ox_note_unsolved';
    let card = '<p class="chm-ans-l">' + esc(t('ox_ans')) + '</p><p class="chm-oxeq">' + big + '</p>';
    card += '<p class="chm-verdict' + (ox.status === 'exact' ? ' good' : ' warn') + '">' + esc(t(statusKey)) + '</p>' + notesHtmlSpecies(p);
    $('chmCardBody').innerHTML = card;
    flash($('chmCardBody'));
    let h = '<p class="chm-oxeq">' + big + '</p><p class="chm-h">' + esc(t('ox_h_rules')) + '</p><ol class="chm-steps">';
    const lines = [];
    for (const rule of ox.rules) {
        // Степени — в записи страны (+7 или +VII); заряд иона (:q) — число, это заряд, а не степень.
        const v = rule.v ? oxShow(rule.v) : '';
        const txt = t(rule.key, { el: rule.el || '', v, ion: rule.ion ? formulaUnicode(rule.ion, rule.q || 0) : '', q: rule.q != null ? (rule.q > 0 ? '+' + rule.q : String(rule.q).replace('-', '−')) : '', el2: rule.el2 || '', v2: rule.v2 ? oxShow(rule.v2) : '', name: rule.el ? elName(rule.el) : '' });
        const eqText = rule.eq ? oxEqShow(rule.eq) : '';
        h += '<li><span class="chm-why">' + esc(txt) + '</span>' + (rule.eq ? '<span class="chm-math" dir="ltr">' + esc(eqText) + '  ⇒  x = ' + esc(v) + '</span>' : '') + '</li>';
        lines.push(txt + (rule.eq ? '  ' + eqText + ' ⇒ x = ' + v : ''));
    }
    h += '</ol>';
    if (ox.status !== 'exact') h += '<p class="chm-why">' + esc(t(statusKey)) + '</p>';
    $('chmSheetTitle').textContent = t('sheet_ox');
    renderMethods([]);
    $('chmSheetBody').innerHTML = h;
    const oxLine = ox.atoms.map((a) => a.el + (ox.byId[a.id] ? oxSupText(oxText(ox.byId[a.id])) : '')).join(' ');
    state.copyText = [speciesText(sp) + ':  ' + oxLine].concat(lines).join('\n');
}

/* ── калькуляторы ────────────────────────────────────────────────────────── */
function setCalc(id) {
    if (CALCS.indexOf(id) < 0) id = 'convert';
    state.calc = id;
    document.querySelectorAll('[data-chm-calc]').forEach((b) => {
        const on = b.getAttribute('data-chm-calc') === id;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    document.querySelectorAll('[data-chm-pane]').forEach((p) => { p.hidden = p.getAttribute('data-chm-pane') !== id; });
}

function renderCalc(fromInput) {
    clearError();
    const res = computeCalc(state.calc, env());
    $('chmSheetTitle').textContent = res.title;
    renderMethods([]);
    if (!res.ok) {
        $('chmCardBody').innerHTML = '<p class="chm-verdict warn">' + esc(res.error || '') + '</p>';
        $('chmSheetBody').innerHTML = '<p class="chm-why">' + esc(res.error || '') + '</p>';
        $('chmCard').hidden = false;
        $('chmSheet').hidden = false;
        $('chmEmpty').hidden = true;
        state.copyText = '';
        state.view = null;
        if (fromInput && !isAuto()) {
            stage('input_refused', { reason: 'calc_' + (res.code || 'error') });
            diag('calc_' + (res.code || 'error'), { mode: 'calc', id: state.calc }, res.input || '');
        }
        return;
    }
    // ответ-формула кода — изолятом слева направо («w = 10 %» в арабском абзаце), фраза перевода — как есть
    const ansHtml = res.math ? ltrMath(res.answer) : esc(res.answer);
    $('chmCardBody').innerHTML = '<p class="chm-ans-l">' + esc(res.title) + '</p><p class="chm-big" dir="ltr">' + esc(res.big || res.answer) + '</p><p class="chm-verdict">' + ansHtml + '</p>';
    let h = '<ol class="chm-steps">' + res.lines.map((l) => '<li><span class="chm-math" dir="auto">' + esc(l) + '</span></li>').join('') + '</ol>';
    if (res.extraHtml) h += res.extraHtml;
    for (const n of (res.notes || [])) h += '<p class="chm-why">' + esc(n) + '</p>';
    h += '<p class="chm-answer" dir="auto">' + esc(t('h_answer')) + ' ' + ansHtml + '</p>';
    $('chmSheetBody').innerHTML = h;
    showResult(true);
    state.copyText = [res.title].concat(res.lines).concat(['', t('h_answer') + ' ' + res.answer]).join('\n');
    state.view = { kind: 'calc' };
    if (fromInput && !isAuto()) {
        stage('solved', { mode: 'calc' });
        complete('calc|' + state.calc + '|' + state.copyText, { mode: 'calc', kind: state.calc, gas: /convert|empirical|density/.test(state.calc) ? state.cond : '' });
    }
}

/* ── настройки ───────────────────────────────────────────────────────────── */
function syncSettingsUi() {
    document.querySelectorAll('[data-chm-mass]').forEach((b) => {
        const on = b.getAttribute('data-chm-mass') === state.mass;
        b.classList.toggle('on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    $('chmMassHint').textContent = t(state.mass === 'precise' ? 'mass_hint_precise' : 'mass_hint_school');
    document.querySelectorAll('input[name="chmCond"]').forEach((r) => { r.checked = r.value === state.cond; });
    $('chmCondCustom').hidden = state.cond !== 'custom';
    $('chmCondT').value = state.condT;
    $('chmCondP').value = state.condP;
    const e = env();
    const bad = state.cond === 'custom' && !(parseNum(state.condT) > -273.15 && parseNum(state.condP) > 0);
    $('chmCondHint').textContent = bad ? t('cond_bad') : t('cond_vm', { vm: F.num(e.Vm), cond: e.condLabel });
    $('chmSetSub').textContent = t('set_sub', { m: t(state.mass === 'precise' ? 'mass_precise' : 'mass_school'), vm: F.num(e.Vm) });
}

/* ── ссылка ──────────────────────────────────────────────────────────────── */
function saveHash() {
    const q = new URLSearchParams();
    q.set('m', state.mode);
    if (state.mode === 'calc') {
        q.set('c', state.calc);
        const fields = (PANE_FIELDS[state.calc] || []).map((id) => {
            const el = $(id);
            if (!el) return '';
            return id + '~' + (el.type === 'checkbox' ? (el.checked ? '1' : '0') : el.value);
        }).filter(Boolean);
        if (fields.length) q.set('x', fields.join(';'));
        // строки «элемент — доля» живут не в разметке, а в модуле: без них ссылка открывала пример C/H
        if (state.calc === 'empirical') q.set('er', empRows().map((r) => r.el + '~' + r.w).join(';'));
    } else {
        const key = { balance: 'e', stoich: 's', molar: 'f', ox: 'o' }[state.mode];
        q.set(key, $('chmInput').value);
        if (state.mode === 'stoich') STO.stoichParams(q);
        if (state.method && state.mode === 'balance') q.set('v', state.method);
    }
    if (state.mass === 'precise') q.set('w', 'p');
    if (state.cond !== 'stp') { q.set('cd', state.cond); if (state.cond === 'custom') { q.set('tt', state.condT); q.set('pp', state.condP); } }
    try { history.replaceState(null, '', '#' + q.toString()); } catch (_) { /* песочница */ }
}

function loadHash() {
    const h = String(location.hash || '').replace(/^#/, '');
    if (!h) return false;
    const q = new URLSearchParams(h);
    const mode = q.get('m');
    if (!mode || MODES.indexOf(mode) < 0) return false;
    if (q.get('w') === 'p') state.mass = 'precise';
    if (q.get('cd')) { state.cond = q.get('cd'); state.condT = q.get('tt') || state.condT; state.condP = q.get('pp') || state.condP; }
    if (mode === 'calc') {
        setMode('calc', { silent: true });
        setCalc(q.get('c') || 'convert');
        for (const part of String(q.get('x') || '').split(';')) {
            const i = part.indexOf('~');
            if (i < 0) continue;
            const el = $(part.slice(0, i));
            if (!el) continue;
            if (el.type === 'checkbox') el.checked = part.slice(i + 1) === '1'; else el.value = part.slice(i + 1);
        }
        if (q.get('er')) {
            setEmpRows(String(q.get('er')).split(';').map((part) => { const i = part.indexOf('~'); return i < 0 ? null : { el: part.slice(0, i), w: part.slice(i + 1) }; }));
            buildEmpRows();
        }
        return true;
    }
    const key = { balance: 'e', stoich: 's', molar: 'f', ox: 'o' }[mode];
    const v = q.get(key) || '';
    state.src[mode] = v;
    state.visited[mode] = true;
    setMode(mode, { silent: true, method: q.get('v') || null });
    $('chmInput').value = v;
    if (mode === 'stoich') {
        let n = 0;
        try { n = parseEquation(v, TABLE).eq.terms.length; } catch (_) { n = 0; }
        state.stoichPreset = n ? STO.stoichPreset(q, n) : null;
    }
    return true;
}

/* ── недавние записи ─────────────────────────────────────────────────────────
 * Домашнее задание — это список реакций: к третьей хочется вернуться к первой. Запись попадает
 * сюда, когда на неё получен ответ и она отстоялась (тот же момент, что «человек получил
 * результат»); хранится на устройстве общим примитивом настроек, наружу не уходит. */
const RECENT_MAX = 8;
function recentLoad() {
    const v = settings.load('recent', []);
    if (!Array.isArray(v)) return [];
    return v.filter((x) => x && typeof x.v === 'string' && x.v && MODES.indexOf(x.m) >= 0 && x.m !== 'calc').slice(0, RECENT_MAX);
}
function recentAdd(mode, value) {
    const v = String(value || '').replace(/\s+/g, ' ').trim().slice(0, 160);
    if (!v) return;
    const list = recentLoad().filter((x) => !(x.m === mode && x.v === v));
    list.unshift({ m: mode, v });
    settings.save('recent', list.slice(0, RECENT_MAX));
    renderRecent();
}
function renderRecent() {
    const box = $('chmRecent');
    if (!box) return;
    const list = recentLoad();
    box.hidden = !list.length;
    $('chmRecentChips').innerHTML = list.map((x, i) => '<button type="button" class="tc-chip chm-mono" dir="ltr" data-chm-recent="' + i + '" title="' + esc(t('mode_' + x.m)) + '">' + esc(x.v) + '</button>').join('');
}

/* ── выдача ──────────────────────────────────────────────────────────────── */
/** Кнопки выдачи живут, только когда есть что отдавать: без решения «Скопировать» молчала. */
function syncActions() {
    const has = !!state.copyText && !$('chmSheet').hidden;
    ['chmCopy', 'chmPdf', 'chmPrint'].forEach((id) => { const b = $(id); if (b) b.disabled = !has; });
}

function copySolution() {
    const text = state.copyText;
    if (!text) return;
    settleNow();
    // запись в буфер — первым делом в обработчике нажатия: после await Safari её уже не разрешит
    const done = window.__copyText ? window.__copyText(text) : navigator.clipboard.writeText(text);
    Promise.resolve(done).then(() => {
        toast(t('copied'), 'success');
        stage('export', { via: 'copy' });
        funnel('export', { via: 'copy', mode: state.mode, len: text.length });
    }, () => {
        toast(t('copy_failed'), 'error');
        funnel('export_failed', { via: 'copy', mode: state.mode });
    });
}

function buildReport() {
    const root = document.createElement('div');
    root.className = 'lg-app chm chm-report';
    const title = document.createElement('p');
    title.className = 'chm-h';
    title.textContent = $('chmSheetTitle').textContent;
    root.appendChild(title);
    const body = $('chmSheetBody').cloneNode(true);
    body.removeAttribute('id');
    body.querySelectorAll('button').forEach((b) => b.remove());
    root.appendChild(body);
    return root;
}

function savePdf() {
    if ($('chmSheet').hidden) return;
    settleNow();
    const btn = $('chmPdf');
    btn.disabled = true;
    const mode = state.mode;
    const base = mode === 'calc' ? state.calc : ($('chmInput').value || 'chemistry');
    let name = 'chemistry.pdf';
    try { if (window.__outputName) name = window.__outputName.join(window.__outputName.sanitize(String(base).replace(/\s+/g, '').slice(0, 40) || 'chemistry'), 'pdf'); } catch (_) { /* имя по умолчанию */ }
    // Сборщик PDF — отдельный файл: не доехал при загрузке страницы — добираем по нажатию, а не молчим.
    ensureScript('__generatePDF', 'report-pdf.js').then((ready) => {
        if (!ready) {
            toast(t('pdf_error'), 'error');
            critical('asset_failed_report_pdf', { asset: 'report-pdf.js' });   // тип свой у каждого файла: общий слой склеивает одинаковые за 30 с
            funnel('export_failed', { via: 'pdf', mode, code: 'script' });
            return null;
        }
        return window.__generatePDF({
            element: buildReport(),
            title: t('pdf_title'),
            filename: name,
            strings: { libError: t('pdf_error'), genError: t('pdf_error'), blocked: t('pdf_blocked') },
            pagebreak: { mode: ['css', 'legacy'] },
        });
    }).then((r) => {
        if (!r) return;
        if (r.ok) { stage('export', { via: 'pdf' }); funnel('export', { via: 'pdf', mode, ms: r.ms || 0, kb: r.kb || 0 }); }
        else funnel('export_failed', { via: 'pdf', mode, code: r.code || '' });
    }).catch((e) => caught(e, 'chm_pdf', t('pdf_error'))).then(() => { syncActions(); });
}

function printSheet() {
    if ($('chmSheet').hidden) return;
    settleNow();
    stage('export', { via: 'print' });
    funnel('export', { via: 'print', mode: state.mode });
    window.print();
}

function share() {
    saveHash();
    settleNow();
    const url = location.href;
    const viaClipboard = () => {
        const done = window.__copyText ? window.__copyText(url) : navigator.clipboard.writeText(url);
        return Promise.resolve(done).then(() => {
            toast(t('link_copied'), 'success');
            funnel('share', { via: 'clipboard', mode: state.mode });
        }, () => { toast(t('copy_failed'), 'error'); funnel('export_failed', { via: 'share', mode: state.mode }); });
    };
    // Системное «Поделиться» — на телефоне; на компьютере ссылку ждут в буфере.
    const phone = !!(navigator.share && window.matchMedia && window.matchMedia('(pointer:coarse)').matches);
    if (!phone) { viaClipboard(); return; }
    navigator.share({ title: document.title, url }).then(() => {
        funnel('share', { via: 'native', mode: state.mode });
    }, (e) => { if (!e || e.name !== 'AbortError') viaClipboard(); });
}

/* ── ввод с клавиатуры химика ────────────────────────────────────────────── */
let lastField = null;
function insertAtCaret(field, ins) {
    const a = field.selectionStart == null ? field.value.length : field.selectionStart;
    const z = field.selectionEnd == null ? a : field.selectionEnd;
    field.value = field.value.slice(0, a) + ins + field.value.slice(z);
    const pos = a + ins.length;
    try { field.setSelectionRange(pos, pos); } catch (_) { /* поле без выделения */ }
    // На телефоне фокус в поле поднимает системную клавиатуру, и она закрывает ту самую панель,
    // по которой человек набирает: фокус возвращаем, только если он в поле уже был (или это мышь).
    if (document.activeElement === field || !coarsePointer()) field.focus();
}
const coarsePointer = () => { try { return !!(window.matchMedia && window.matchMedia('(pointer:coarse)').matches); } catch (_) { return false; } };

/* ── запуск ──────────────────────────────────────────────────────────────── */
function init() {
    const input = $('chmInput');
    if (!input) return;
    // Подписи, которые отрисовал сервер («22,4 L/mol», «C 85,7 %»), — десятичным знаком записи:
    // на испанской странице он зависит от страны читателя (Мексика — 22.4).
    document.querySelectorAll('[data-chm-dec]').forEach((el) => { el.textContent = locText(el.textContent); });
    state.mass = settings.load('mass', defaultMassMode(LOCALE)) === 'precise' ? 'precise' : 'school';
    state.cond = settings.load('cond', 'stp');
    state.condT = String(settings.load('condT', '25'));
    state.condP = String(settings.load('condP', DEFAULT_P));
    if (['stp', 'iupac', 'satp', 'room', 'custom'].indexOf(state.cond) < 0) state.cond = 'stp';

    let timer = 0;
    input.addEventListener('input', () => {
        touched();
        clearTimeout(timer);
        timer = setTimeout(() => { followInput(); refresh(true); }, 300);
    });
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { touched(); clearTimeout(timer); followInput(); refresh(true); } });
    const trackable = (el) => el && el.matches && el.matches('#chmInput, #cvFormula, #moFormula, #dnFormula, [data-emp-el]');
    document.getElementById('chmApp').addEventListener('focusin', (e) => { if (trackable(e.target)) lastField = e.target; });

    document.querySelectorAll('[data-chm-mode]').forEach((b) => b.addEventListener('click', () => {
        setMode(b.getAttribute('data-chm-mode'));
        refresh(true);
    }));
    document.querySelector('.chm-modes').addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const i = MODES.indexOf(state.mode);
        const dir = (document.documentElement.dir === 'rtl' ? -1 : 1) * (e.key === 'ArrowRight' ? 1 : -1);
        const next = MODES[(i + dir + MODES.length) % MODES.length];
        setMode(next);
        refresh(true);
        const b = document.querySelector('[data-chm-mode="' + next + '"]');
        if (b) b.focus();
    });

    // нажатие клавиши панели не забирает фокус у поля: каретка и системная клавиатура остаются как были
    $('chmKeys').addEventListener('pointerdown', (e) => { if (e.target.closest('[data-chm-key]')) e.preventDefault(); });
    document.querySelectorAll('[data-chm-key]').forEach((b) => b.addEventListener('click', (e) => {
        e.preventDefault();
        touched();
        insertAtCaret(input, b.getAttribute('data-chm-key'));
        followInput();
        refresh(true);
        ae('key', { k: b.getAttribute('data-chm-key').trim() });
    }));
    $('chmPtKey').addEventListener('click', () => {
        const box = $('chmPtBox');
        box.open = true;
        buildPeriodicTable(pickElement, () => state.mass);
        const first = box.querySelector('[data-pt]');
        if (first) first.scrollIntoView({ block: 'nearest' });
    });
    $('chmPtBox').addEventListener('toggle', () => { if ($('chmPtBox').open) buildPeriodicTable(pickElement, () => state.mass); });
    $('chmRefBox').addEventListener('toggle', () => {
        if (!$('chmRefBox').open) return;
        loadReference();
        funnel('reference', { what: 'solubility' });
    });
    $('chmRefBox').addEventListener('click', (e) => { if (e.target.closest('[data-chm-retry="ref"]')) loadReference(); });

    document.querySelectorAll('[data-chm-grade]').forEach((b) => b.addEventListener('click', () => {
        state.grade = b.getAttribute('data-chm-grade');
        document.querySelectorAll('[data-chm-grade]').forEach((x) => {
            const on = x === b;
            x.classList.toggle('on', on);
            x.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        document.querySelectorAll('[data-chm-sample]').forEach((x) => { x.hidden = x.getAttribute('data-chm-group') !== state.grade; });
    }));
    document.querySelectorAll('[data-chm-sample]').forEach((b) => b.addEventListener('click', () => {
        touched();
        input.value = b.getAttribute('data-chm-sample');
        if (state.mode === 'stoich') STO.ST.key = '';
        refresh(true);
        funnel('example', { mode: state.mode, from: 'chips', group: b.getAttribute('data-chm-group') });
    }));

    // Делегирование: кнопки внутри ответа и ошибок
    $('chmApp').addEventListener('click', (e) => {
        const pick = e.target.closest('[data-chm-pick]');
        if (pick) { touched(); applyPick(pick.getAttribute('data-chm-pick'), pick.getAttribute('data-chm-charge')); return; }
        const set = e.target.closest('[data-chm-set]');
        if (set) { touched(); input.value = set.getAttribute('data-chm-set'); refresh(true); ae('set_suggest', {}); return; }
        const go = e.target.closest('[data-chm-go]');
        if (go) { const v = input.value; setMode(go.getAttribute('data-chm-go'), { keep: true }); input.value = v; touched(); refresh(true); return; }
        const near = e.target.closest('[data-chm-near]');
        if (near) {
            const at = near.getAttribute('data-chm-at');
            const bad = near.getAttribute('data-chm-bad');
            const v = input.value;
            const i = at !== '' && v.slice(+at, +at + bad.length) === bad ? +at : v.indexOf(bad);
            if (i >= 0) input.value = v.slice(0, i) + near.getAttribute('data-chm-near') + v.slice(i + bad.length);
            touched();
            refresh(true);
            ae('near_pick', {});
            return;
        }
        const ex = e.target.closest('[data-chm-example]');
        if (ex) {
            const mode = ex.getAttribute('data-chm-example-mode');
            if (mode !== state.mode) setMode(mode, { keep: true });
            if (mode === 'stoich') STO.ST.key = '';
            input.value = ex.getAttribute('data-chm-example');
            touched();
            refresh(true);
            funnel('example', { mode, from: 'empty' });
            return;
        }
        const conv = e.target.closest('[data-chm-convert]');
        if (conv) {
            setMode('calc');
            setCalc('convert');
            $('cvFormula').value = conv.getAttribute('data-chm-convert');
            touched();
            refresh(true);
            return;
        }
        const rec = e.target.closest('[data-chm-recent]');
        if (rec) {
            const x = recentLoad()[+rec.getAttribute('data-chm-recent')];
            if (!x) return;
            if (x.m !== state.mode) setMode(x.m, { keep: true });
            if (x.m === 'stoich') STO.ST.key = '';
            input.value = x.v;
            touched();
            refresh(true);
            funnel('recent', { mode: x.m });
            return;
        }
        if (e.target.closest('#chmRecentClear')) { settings.save('recent', []); renderRecent(); funnel('recent_clear', {}); return; }
        const meth = e.target.closest('[data-chm-method]');
        if (meth) {
            const id = meth.getAttribute('data-chm-method');
            if (id === state.method) return;
            state.method = id;
            const apply = () => { if (state.view) renderBalance(state.view); };
            if (window.__viewTransition) window.__viewTransition(apply); else apply();
            saveHash();
            funnel('method', { id });
        }
    });

    // калькуляторы
    document.querySelectorAll('[data-chm-calc]').forEach((b) => b.addEventListener('click', () => {
        const id = b.getAttribute('data-chm-calc');
        // панель открывается с примером в полях — это ещё не расчёт человека
        if (id !== state.calc && state.auto['calc:' + id] == null) state.auto['calc:' + id] = true;
        const changed = id !== state.calc;
        setCalc(id);
        refresh(true);
        if (changed) funnel('calc', { id });
    }));
    let ctimer = 0;
    $('chmCalcIn').addEventListener('input', (e) => {
        touched();
        if (e.target.closest('#empRows')) return;
        clearTimeout(ctimer);
        ctimer = setTimeout(() => refresh(true), 250);
    });
    $('chmCalcIn').addEventListener('change', (e) => { if (e.target.tagName === 'SELECT' || e.target.type === 'checkbox') { touched(); refresh(true); } });
    $('chmCalcIn').addEventListener('click', (e) => { if (e.target.closest('[data-emp-del], #empAdd')) touched(); });
    buildEmpRows();
    bindEmp(() => { clearTimeout(ctimer); ctimer = setTimeout(() => refresh(true), 250); });

    // задача
    STO.bindStoich(() => (state.view && state.view.eq) || null, () => { if (state.mode === 'stoich') { touched(); refresh(true); } });

    // настройки
    document.querySelectorAll('[data-chm-mass]').forEach((b) => b.addEventListener('click', () => {
        state.mass = b.getAttribute('data-chm-mass');
        settings.save('mass', state.mass);
        syncSettingsUi();
        refresh(false);
        funnel('setting', { k: 'mass', v: state.mass });
    }));
    $('chmCond').addEventListener('change', (e) => {
        const r = e.target;
        if (!r || r.name !== 'chmCond' || !r.checked) return;
        state.cond = r.value;
        settings.save('cond', state.cond);
        syncSettingsUi();
        refresh(false);
        funnel('setting', { k: 'cond', v: state.cond });
    });
    ['chmCondT', 'chmCondP'].forEach((id) => $(id).addEventListener('input', () => {
        state.condT = $('chmCondT').value;
        state.condP = $('chmCondP').value;
        settings.save('condT', state.condT);
        settings.save('condP', state.condP);
        syncSettingsUi();
        refresh(false);
    }));

    // тренажёр
    $('chmTrThis').addEventListener('click', () => trainerStart(state.mode === 'balance' || state.mode === 'stoich' ? input.value : ''));
    $('chmTrNew').addEventListener('click', trainerRandom);
    $('chmTrCheck').addEventListener('click', trainerCheck);
    $('chmTrShow').addEventListener('click', trainerShow);
    $('chmTrEq').addEventListener('keydown', (e) => { if (e.key === 'Enter') trainerCheck(); });

    // выдача
    $('chmCopy').addEventListener('click', copySolution);
    $('chmPdf').addEventListener('click', savePdf);
    $('chmPrint').addEventListener('click', printSheet);
    $('chmShare').addEventListener('click', share);

    state.auto['calc:convert'] = true;
    if (!loadHash()) {
        setMode('balance', { silent: true });
    }
    syncSettingsUi();
    renderRecent();
    refresh(false);

    window.__chmState = state;
}

/** Таблица растворимости — отдельный файл. Не доехал — говорим об этом и даём «Повторить». */
let refLoading = false;
function loadReference() {
    if (refLoading || buildReference()) return;
    refLoading = true;
    ensureScript('SolubilityTable', 'solubility-table.js').then((ready) => {
        refLoading = false;
        if (ready && buildReference()) return;
        $('chmSolWrap').innerHTML = '<p class="chm-err" role="alert">' + esc(t('ref_failed')) + ' <button type="button" class="tc-chip" data-chm-retry="ref">' + esc(t('retry')) + '</button></p>';
        critical('asset_failed_solubility', { asset: 'solubility-table.js' });
    });
}

function pickElement(sym) {
    const field = lastField && document.body.contains(lastField) && !lastField.closest('[hidden]') ? lastField : $('chmInput');
    if (field === $('chmInput') && state.mode === 'calc') { setMode('balance', { keep: false }); }
    insertAtCaret(field, sym);
    touched();
    if (field.id === 'chmInput') { followInput(); refresh(true); } else field.dispatchEvent(new Event('input', { bubbles: true }));
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
